/**
 * The Familiar (issue #13): a companion built from the familiar-abilities Pack whose statistics
 * derive from the master's sheet, as Foundry's FamiliarPF2e derives them from its master.
 *
 * - Ability slots: the master's Entries change `system.attributes.familiarAbilities.value` with
 *   ActiveEffectLike Rule Elements (a witch's Familiar feature, Pet, Enhanced Familiar, the
 *   Familiar Master dedication). The slot track re-resolves those Rule Elements at every Level up
 *   to the master's, so the guide opens one `familiar-ability` Choice at the Level each slot
 *   appears (ADR-0006).
 * - Fixed abilities: a patron's familiar ability (named in the patron feature's text), the
 *   Construct ability of an alchemical familiar, a draconic familiar's Dragon ability. Foundry's
 *   data carries no Rule Element for these; a small table and the patron text supply them. They
 *   are derived like grants, never Choices, and count against the slots where the rules say so.
 * - Statistics (Player Core, "Familiars"): HP 5 per Level; AC and saves equal the master's before
 *   status and circumstance modifiers; Perception, Acrobatics and Stealth are the master's Level
 *   plus the master's spellcasting attribute modifier (at least +3); every other skill and attack
 *   roll is the master's Level; Speed 25, Tiny, low-light vision; item bonuses never apply.
 * - The abilities' Rule Elements run through the same engine with the Familiar as the actor:
 *   BaseSpeed (Flier), Sense (Darkvision), FlatModifier (Tough, Skilled), Resistance, ActorTraits.
 */
import type { Character, Choice, LevelNumber, Override } from "../character/model";
import { choiceKey, parseChoiceKey } from "../character/model";
import { createActorData, getProperty, type ActorData } from "./actor-data";
import type { DerivedSheet, HitPointsView, ItemSummary, IWRView, RuleProblem, SpeedViews, StatisticView, UnsupportedRule } from "./engine";
import { evaluateFormula, replaceFormulaData } from "./formula";
import { emptyInventoryState } from "./inventory";
import { EngineItem } from "./items";
import { createModifier, resolveModifiers, type Modifier } from "./modifiers";
import { prepareMovement } from "./movement";
import { setDerivedRollOptions, setNumericRollOptions } from "./prepare";
import { bracketsToFormula } from "./resolve";
import { RollOptions } from "./roll-options";
import { ChangeFailure, getNewValue, type AELikeMode } from "./rule-elements/ae-like";
import type { RuleInstance } from "./rule-elements/base";
import { classifyKey } from "./rule-elements/registry";
import { collectPrompts, createRules, createRulesFor, readSelector, runPhase, type ChoicePrompt } from "./rules";
import { prepareSenses, senseViews, type SenseView } from "./senses";
import type { DerivedStatistics, EngineState } from "./state";
import { breakdownOf, buildStatistic, type DerivedStatistic } from "./statistics";
import { createSynthetics, extractModifiers } from "./synthetics";
import { createOverrideLookup, iwrView, modifierView, speedView, statisticView } from "./views";
import { ATTRIBUTE_LABELS, MOVEMENT_TYPES, SAVES, SAVE_ATTRIBUTES, SAVE_LABELS, SKILLS, SKILL_SLUGS, isAttribute, type Attribute, type SaveSlug } from "./values";

/** The familiar-abilities Pack's collection id. */
export const FAMILIAR_PACK = "familiar-abilities";
/** The actor path the master's Rule Elements change to give ability slots (Foundry: `CharacterPF2e` attributes). */
export const FAMILIAR_ABILITIES_PATH = "system.attributes.familiarAbilities.value";

/**
 * Master abilities: the abilities of the familiar-abilities Pack that benefit the master rather
 * than the familiar. Foundry's data files all carry `category: "familiar"`; the split is the
 * printed one (Player Core and later books, as Archives of Nethys lists them).
 */
export const MASTER_ABILITY_SLUGS: ReadonlySet<string> = new Set([
  "absorb-familiar", "cantrip-connection", "extra-alchemy", "extra-vial", "familiar-focus", "innate-surge", "kindling", "lifelink",
  "recall-familiar", "restorative-familiar", "share-senses", "spell-battery", "spell-delivery", "tattoo-transformation",
]);

/**
 * Abilities a granting Entry fixes on the Familiar, by the Entry's slug (the text says so; no Rule
 * Element does). `countsAgainstSlots` false: the ability is free (an alchemical familiar's Construct).
 */
export const FIXED_FAMILIAR_ABILITIES: Readonly<Record<string, { abilities: string[]; countsAgainstSlots: boolean }>> = Object.freeze({
  "alchemical-familiar": { abilities: ["construct"], countsAgainstSlots: false },
  "draconic-familiar": { abilities: ["dragon"], countsAgainstSlots: true },
  "psychopomp-familiar": { abilities: ["speech"], countsAgainstSlots: true },
  "emissary-familiar": { abilities: ["synchronize-spirit"], countsAgainstSlots: true },
  "elver-pet": { abilities: ["fast-movement", "damage-avoidance"], countsAgainstSlots: true },
});

/** The pattern a witch patron feature names its familiar ability with (a bold heading in Foundry's text). */
const PATRON_ABILITY_PATTERN = /<strong>\s*(Familiar of [^<]+?)\s*<\/strong>/;

export interface FixedFamiliarAbility {
  slug: string;
  /** The Entry's UUID, when the Pack has it; null when the name is unknown to this Snapshot. */
  uuid: string | null;
  name: string;
  /** The Entry that fixes the ability and the Level it was taken at. */
  source: string;
  sourceName: string;
  level: LevelNumber;
  countsAgainstSlots: boolean;
}

export interface FamiliarSlotTrack {
  /** Ability slots the master's Entries give at each Level (index is the Level; [0] unused). */
  total: number[];
  fixed: FixedFamiliarAbility[];
  /** Free `familiar-ability` Choices opened at each Level: the new slots less the fixed abilities that arrive then. */
  opened: number[];
}

interface AELikePathData {
  mode: AELikeMode;
  path: string;
  value: unknown;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** The Level an item was taken at: its Choice's Level, or the master's for a Session Entry. */
function itemLevel(state: EngineState, item: EngineItem): LevelNumber {
  return parseChoiceKey(item.choiceKey ?? "")?.level ?? (state.level as LevelNumber);
}

/** The ActiveEffectLike Rule Elements on the master that change the ability slot count, active now, in application order. */
function slotRules(state: EngineState): RuleInstance[] {
  return state.rules.filter((r) => r.key === "ActiveEffectLike" && r.handler && !r.ignored && (r.data as unknown as AELikePathData).path === FAMILIAR_ABILITIES_PATH && r.test(state));
}

/** A slot Rule Element's value with the master at another Level (`@actor.level` is the only variable these formulas read). */
function valueAtLevel(state: EngineState, rule: RuleInstance, level: number): number {
  const raw = (rule.data as unknown as AELikePathData).value;
  const formula = typeof raw === "number" ? raw : typeof raw === "string" ? raw : isPlainObject(raw) && "brackets" in raw ? bracketsToFormula(raw) : null;
  if (typeof formula === "number") return formula;
  if (formula === null) return 0;
  const system = state.actor.system;
  const actor = { ...state.actorView, level, system: { ...system, details: { ...system.details, level: { value: level } } } };
  const { formula: replaced, unresolved } = replaceFormulaData(formula, (path) => getProperty({ actor, item: rule.item }, path));
  if (unresolved.length > 0) return 0;
  try {
    const value = evaluateFormula(replaced);
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}

/** The slot count the rules give at a Level, applying only the Rule Elements of Entries taken by then. */
function slotsAt(state: EngineState, rules: RuleInstance[], level: number, exclude?: Set<EngineItem>): number {
  let value = 0;
  for (const rule of rules) {
    if (exclude?.has(rule.item)) continue;
    if (itemLevel(state, rule.item) > level) continue;
    const next = getNewValue((rule.data as unknown as AELikePathData).mode, value, valueAtLevel(state, rule, level));
    if (next instanceof ChangeFailure || typeof next !== "number") continue;
    value = next;
  }
  return Math.max(0, Math.trunc(value));
}

/** A familiar-abilities record by slug or name: the catalogue's, or a loaded Entry's. */
function abilityRecord(state: EngineState, by: { slug?: string; name?: string }): { u: string; n: string; s: string } | undefined {
  const matches = (record: { p: string; s: string; n: string }): boolean => record.p === FAMILIAR_PACK && (by.slug !== undefined ? record.s === by.slug : record.n === by.name);
  const fromCatalogue = state.catalogue?.records.find(matches);
  if (fromCatalogue) return { u: fromCatalogue.u, n: fromCatalogue.n, s: fromCatalogue.s };
  for (const entry of state.entries.values()) if (matches({ p: entry.pack, s: entry.slug, n: entry.name })) return { u: entry.uuid, n: entry.name, s: entry.slug };
  return undefined;
}

/** The abilities the master's Entries fix on the Familiar (a patron's, an alchemical familiar's Construct ...), first source wins. */
export function fixedFamiliarAbilities(state: EngineState): FixedFamiliarAbility[] {
  const out: FixedFamiliarAbility[] = [];
  const seen = new Set<string>();
  for (const item of state.items) {
    if (!item.choiceKey || item.sessionId || item.inMemory || !item.active) continue;
    const level = itemLevel(state, item);
    const push = (slug: string, name: string | null, countsAgainstSlots: boolean): void => {
      if (seen.has(slug)) return;
      seen.add(slug);
      const record = abilityRecord(state, { slug });
      out.push({ slug, uuid: record?.u ?? null, name: record?.n ?? name ?? slug, source: item.uuid, sourceName: item.name, level, countsAgainstSlots });
    };
    const table = FIXED_FAMILIAR_ABILITIES[item.slug];
    if (table) for (const slug of table.abilities) push(slug, null, table.countsAgainstSlots);
    if (item.otherTags.includes("witch-patron")) {
      const name = PATRON_ABILITY_PATTERN.exec(item.description)?.[1]?.trim();
      const record = name ? abilityRecord(state, { name }) : undefined;
      if (record) push(record.s, record.n, true);
    }
  }
  return out;
}

/** The slot track of a derived master: totals and free Choices at every Level up to its own. */
export function familiarSlotTrack(state: EngineState): FamiliarSlotTrack {
  const rules = slotRules(state);
  const fixed = fixedFamiliarAbilities(state);
  const total: number[] = [0];
  const opened: number[] = [0];
  for (let level = 1; level <= state.level; level++) {
    total[level] = slotsAt(state, rules, level);
    const arriving = fixed.filter((f) => f.countsAgainstSlots && f.level === level).length;
    opened[level] = Math.max(0, total[level]! - total[level - 1]! - arriving);
  }
  return { total, fixed, opened };
}

/**
 * The slot count at the master's Level without an Entry's own Rule Elements and those of what it
 * granted: what `self:has-familiar` meant when Foundry created the Entry, before its own upgrade
 * applied (the Familiar Master dedication grants Enhanced Familiar only to a Character that had a
 * familiar already).
 */
export function familiarAbilitiesWithout(state: EngineState, granter: EngineItem): number {
  const exclude = new Set(state.items.filter((i) => i === granter || i.grantChain.includes(granter)));
  return slotsAt(state, slotRules(state), state.level, exclude);
}

/* ---------------------------------- the Familiar ---------------------------------- */

export interface FamiliarAbilityView {
  /** The Choice key of a chosen ability; null for a fixed one. */
  choiceKey: string | null;
  level: LevelNumber;
  /** Null while the Choice is open. */
  uuid: string | null;
  name: string | null;
  slug: string | null;
  /** A master ability (benefits the master) rather than a familiar ability. */
  master: boolean;
  remaster: boolean | null;
  /** Set on an ability the master's Entries fix, with what fixes it. */
  fixed: { source: string; sourceName: string; countsAgainstSlots: boolean } | null;
  /** The Entry was supplied to the engine (its Rule Elements ran). */
  loaded: boolean;
  /** Answers the Entry still wants (open ChoiceSets), by flag. */
  openPrompts: string[];
  /** The Choice exists but no slot opens it at its Level any more (its granting Entry left); the guide reopens it. */
  extra: boolean;
}

export interface FamiliarSlotsView {
  /** Slots at the master's Level. */
  total: number;
  /** Fixed abilities that count against the slots, and those that do not. */
  fixed: number;
  fixedFree: number;
  /** Free Choices opened up to the master's Level, and how many hold an ability. */
  free: number;
  filled: number;
  byLevel: { level: LevelNumber; total: number; opened: number }[];
}

export interface FamiliarView {
  name: string;
  level: number;
  size: string;
  traits: string[];
  /** The master's attribute the Familiar's Perception, Acrobatics and Stealth read. */
  attribute: {
    /** The one in force: the user's choice, else the derived default. */
    value: Attribute;
    chosen: Attribute | null;
    /** The rules' default: the master's spellcasting attribute, else Charisma. */
    derived: Attribute;
    /** The master's modifier for it, and whether the +3 minimum applies instead. */
    modifier: number;
    minimum: boolean;
  };
  slots: FamiliarSlotsView;
  hp: HitPointsView;
  ac: StatisticView;
  saves: Record<SaveSlug, StatisticView>;
  perception: StatisticView;
  /** The rare attack roll: the master's Level (Foundry: `attackStatistic`). */
  attack: StatisticView;
  skills: Record<string, StatisticView>;
  speeds: SpeedViews;
  senses: SenseView[];
  iwr: { immunities: IWRView[]; weaknesses: IWRView[]; resistances: IWRView[] };
  /** The master's DCs the familiar's abilities use (Spell Delivery, a patron's ability). */
  masterDC: { spell: number | null; class: number | null };
  abilities: FamiliarAbilityView[];
  items: ItemSummary[];
  unsupported: UnsupportedRule[];
  problems: RuleProblem[];
  /** Overrides on the Familiar that name no statistic on this sheet. */
  danglingOverrides: Override[];
  missing: { uuid: string; choiceKey: string }[];
  rollOptions: Record<string, string[]>;
}

export interface FamiliarChoice {
  level: LevelNumber;
  choice: Choice;
}

export interface FamiliarResult {
  /** Null when the master has no ability slot and no fixed ability: no Familiar. */
  view: FamiliarView | null;
  /** The abilities' ChoiceSet prompts, keyed by their Choice, for the guide. */
  prompts: ChoicePrompt[];
  /** Entry UUIDs the Familiar wanted and was not given (fixed abilities). */
  needed: string[];
  track: FamiliarSlotTrack;
}

/** Every statistic key an Override on the Familiar may name. */
export function familiarOverrideTargets(view: FamiliarView): { key: string; label: string; derived: number }[] {
  const targets = [
    { key: view.hp.overrideKey, label: "Hit Points", derived: view.hp.derived },
    { key: view.ac.overrideKey, label: view.ac.label, derived: view.ac.derived },
    ...Object.values(view.saves).map((s) => ({ key: s.overrideKey, label: s.label, derived: s.derived })),
    { key: view.perception.overrideKey, label: view.perception.label, derived: view.perception.derived },
    { key: view.attack.overrideKey, label: view.attack.label, derived: view.attack.derived },
    ...Object.values(view.skills).map((s) => ({ key: s.overrideKey, label: s.label, derived: s.derived })),
  ];
  for (const speed of Object.values(view.speeds)) if (speed) targets.push({ key: speed.overrideKey, label: speed.label, derived: speed.derived });
  return targets;
}

/** The rules' default attribute: the master's class spellcasting attribute, else that of any Casting Source, else Charisma. */
export function defaultFamiliarAttribute(master: EngineState): Attribute {
  const sources = master.spellcasting.sources;
  const preferred = sources.find((s) => s.kind === "class") ?? sources.find((s) => s.casting !== "innate") ?? sources[0];
  return preferred?.attribute ?? "cha";
}

/** Whether a Choice is a Familiar ability slot. */
export function isFamiliarAbilityChoice(choice: Choice): choice is Choice & { source: { kind: "progression"; slot: "familiar-ability"; ordinal: number } } {
  return choice.source.kind === "progression" && choice.source.slot === "familiar-ability";
}

function familiarActorData(level: number): ActorData {
  const actor = createActorData(level);
  actor.system.traits = { value: ["minion"], rarity: "common", size: { value: "tiny" } };
  actor.system.perception.senses = [{ type: "low-light-vision", acuity: "precise", range: Infinity, source: null }];
  actor.system.movement.speeds.land = { type: "land", value: 25, base: 25, source: null };
  actor.system.attributes.speed.value = 25;
  actor.system.hands.max = { value: 0, active: 0 };
  actor.system.hands.free = { value: 0, really: 0 };
  actor.system.details.alliance = "party";
  return actor;
}

function masterModifierSum(modifiers: Modifier[]): number {
  return modifiers.filter((m) => m.enabled && m.type !== "status" && m.type !== "circumstance").reduce((sum, m) => sum + m.value, 0);
}

/**
 * Derive the Familiar of a derived master. `choices` are the master's `familiar-ability` Choices
 * (their Entries do not join the master's items: the abilities' Rule Elements are the Familiar's).
 */
export function deriveFamiliar(master: EngineState, character: Character, choices: FamiliarChoice[], masterDerived: DerivedStatistics, mode: "open" | "all"): FamiliarResult {
  const track = familiarSlotTrack(master);
  const level = master.level;
  const total = track.total[level] ?? 0;
  const needed = new Set<string>();
  if (total === 0 && track.fixed.length === 0) return { view: null, prompts: [], needed: [], track };

  const settings = character.familiar;
  const actor = familiarActorData(level);
  const state: EngineState = {
    level,
    actor,
    items: [],
    rules: [],
    rollOptions: new RollOptions(),
    synthetics: createSynthetics(),
    toggles: [],
    toggleState: master.toggleState,
    variantRules: master.variantRules,
    actorView: {},
    entries: master.entries,
    catalogue: master.catalogue,
    needed,
    derivedRanks: {},
    addRules: (item) => {
      const rules = createRulesFor(item, state);
      state.rules.push(...rules);
      return rules;
    },
    inventory: emptyInventoryState(),
    derived: null,
    strikes: [],
    battleForm: null,
    handsReallyFree: 0,
    spellcasting: { sources: [], unrecognised: [] },
  };
  const derivedAttribute = defaultFamiliarAttribute(master);
  const chosenAttribute = settings?.attribute && isAttribute(settings.attribute) ? settings.attribute : null;
  const attribute = chosenAttribute ?? derivedAttribute;
  const masterAttributeModifier = master.actor.system.abilities[attribute].mod;
  state.actorView = {
    name: settings?.name ?? "Familiar",
    type: "familiar",
    level,
    system: actor.system,
    flags: actor.flags,
    abilities: actor.system.abilities,
    attributes: actor.system.attributes,
    skills: new Proxy({} as Record<string, unknown>, {
      get: (_t, slug) => (typeof slug === "string" ? actor.system.skills[slug] : undefined),
      has: (_t, slug) => typeof slug === "string" && slug in actor.system.skills,
    }),
    hitPoints: actor.system.attributes.hp,
    masterAttributeModifier,
    masterAbilityModifier: masterAttributeModifier,
    master: master.actorView,
  };
  state.rollOptions.set("all", "self:type:familiar");
  state.rollOptions.set("all", `self:level:${level}`);
  state.rollOptions.set("all", "self:trait:minion");
  for (const option of master.rollOptions.get()) if (option.startsWith("variant:")) state.rollOptions.set("all", option);
  // "[A familiar] can never benefit from item bonuses" (Foundry: FamiliarPF2e#prepareBaseData).
  (state.synthetics.modifierAdjustments.all ??= []).push({ slug: null, test: (options) => [...options].includes("bonus:type:item"), suppress: true, sourceName: "Familiar" });

  // Items: the chosen abilities, then the fixed ones.
  const missing: FamiliarView["missing"] = [];
  const ordered = [...choices].sort((a, b) => a.level - b.level || (a.choice.source as { ordinal: number }).ordinal - (b.choice.source as { ordinal: number }).ordinal);
  const abilities: FamiliarAbilityView[] = [];
  const itemByKey = new Map<string, EngineItem>();
  for (const { level: at, choice } of ordered) {
    const key = choiceKey(at, choice.source);
    const ordinal = (choice.source as { ordinal: number }).ordinal;
    const extra = ordinal >= (track.opened[at] ?? 0);
    const uuid = choice.selection?.uuid ?? null;
    const entry = uuid ? master.entries.get(uuid) : undefined;
    const record = uuid ? master.catalogue?.byUuid(uuid) : undefined;
    if (uuid && !entry) missing.push({ uuid, choiceKey: key });
    if (entry) {
      const item = new EngineItem({ entry, answers: choice.answers, choiceKey: key, levelTaken: at });
      state.items.push(item);
      itemByKey.set(key, item);
    }
    const slug = entry?.slug ?? record?.s ?? null;
    abilities.push({ choiceKey: key, level: at, uuid, name: entry?.name ?? record?.n ?? null, slug, master: !!slug && MASTER_ABILITY_SLUGS.has(slug), remaster: entry?.publication.remaster ?? record?.rm ?? null, fixed: null, loaded: !!entry, openPrompts: [], extra });
  }
  for (const fixed of track.fixed) {
    const entry = fixed.uuid ? master.entries.get(fixed.uuid) : undefined;
    if (fixed.uuid && !entry) needed.add(fixed.uuid);
    if (entry) state.items.push(new EngineItem({ entry, levelTaken: fixed.level }));
    abilities.push({ choiceKey: null, level: fixed.level, uuid: fixed.uuid, name: fixed.name, slug: fixed.slug, master: MASTER_ABILITY_SLUGS.has(fixed.slug), remaster: entry?.publication.remaster ?? master.catalogue?.byUuid(fixed.uuid ?? "")?.rm ?? null, fixed: { source: fixed.source, sourceName: fixed.sourceName, countsAgainstSlots: fixed.countsAgainstSlots }, loaded: !!entry, openPrompts: [], extra: false });
  }

  // Rule Elements through the same phases as the master's.
  state.rules = createRules(state);
  for (let i = 0; i < state.rules.length; i++) runPhase(state.rules[i]!, state, "onApplyActiveEffects");
  setNumericRollOptions(state);
  setDerivedRollOptions(state);
  for (let i = 0; i < state.rules.length; i++) runPhase(state.rules[i]!, state, "beforePrepareData");

  // Statistics (Foundry: FamiliarPF2e#prepareDerivedData).
  const masterLevel = master.variantRules.proficiencyWithoutLevel ? 0 : level;
  const levelModifier = (): Modifier => createModifier({ slug: "master-level", label: "Master's Level", value: masterLevel, type: "untyped" });
  const minimum = masterAttributeModifier <= 2;
  const attributeModifier = (): Modifier =>
    minimum
      ? createModifier({ slug: "minimum-attribute-modifier", label: "Minimum attribute modifier", value: 3, type: "untyped" })
      : createModifier({ slug: `master-${attribute}`, label: `Master's ${ATTRIBUTE_LABELS[attribute]}`, value: masterAttributeModifier, type: "untyped" });

  const hpModifiers = [createModifier({ slug: "base-hp", label: "5 per Level", value: level * 5, type: "untyped" }), ...extractModifiers(state.synthetics, ["hp"], { test: state.rollOptions.get(["hp"]) })];
  const hp = resolveModifiers(hpModifiers, state.rollOptions.get(["hp"]));
  actor.system.attributes.hp.max = hp.total;
  actor.system.attributes.hp.value = hp.total;

  const masterAC = createModifier({ slug: "master-armor-class", label: "Master's AC", value: masterModifierSum(masterDerived.ac.dc.modifiers), type: "untyped" });
  const ac = buildStatistic(state, { slug: "armor", label: "Armor Class", attribute: null, rank: null, domains: ["all", "dex-based"], dc: { domains: ["ac"], modifiers: [masterAC] } });
  actor.system.attributes.ac.value = ac.dc.value;

  const saves = Object.fromEntries(
    SAVES.map((save) => {
      const masterSave = createModifier({ slug: `master-${save}`, label: `Master's ${SAVE_LABELS[save]}`, value: masterModifierSum(masterDerived.saves[save].check.modifiers), type: "untyped" });
      const statistic = buildStatistic(state, { slug: save, label: SAVE_LABELS[save], attribute: null, rank: null, domains: [save, `${SAVE_ATTRIBUTES[save]}-based`, "saving-throw", "all"], modifiers: [masterSave], check: { type: "saving-throw" } });
      actor.system.saves[save].value = statistic.check.mod;
      return [save, statistic];
    }),
  ) as Record<SaveSlug, DerivedStatistic>;

  const attack = buildStatistic(state, { slug: "attack-roll", label: "Attack Roll", attribute: null, rank: null, domains: ["all"], modifiers: [levelModifier()], check: { type: "attack-roll" } });

  prepareSenses(state);
  const perception = buildStatistic(state, { slug: "perception", label: "Perception", attribute: null, rank: null, domains: ["perception", "wis-based", "all"], modifiers: [levelModifier(), attributeModifier()], check: { type: "perception-check" } });
  actor.system.perception.value = perception.check.mod;

  const skills: Record<string, DerivedStatistic> = {};
  for (const slug of SKILL_SLUGS) {
    const skill = SKILLS[slug];
    const modifiers = [levelModifier()];
    if (slug === "acrobatics" || slug === "stealth") modifiers.push(attributeModifier());
    const statistic = buildStatistic(state, { slug, label: skill.label, attribute: null, rank: null, domains: [slug, `${skill.attribute}-based`, "skill-check", "all"], modifiers, check: { type: "skill-check" } });
    actor.system.skills[slug]!.value = statistic.check.mod;
    skills[slug] = statistic;
  }

  const speeds = prepareMovement(state);
  state.derived = { hp: { modifiers: hp.modifiers, max: hp.total }, ac, saves, perception, initiative: perception, skills, classDCs: {}, spellcasting: null, spellcastingEntries: {}, speeds, dexterityCap: null };
  for (let i = 0; i < state.rules.length; i++) runPhase(state.rules[i]!, state, "afterPrepareData");

  // Views.
  const overrides = settings?.overrides ?? [];
  const { overrideFor, used } = createOverrideLookup(overrides);
  const view = (statistic: DerivedStatistic, key: string, useDC = false): StatisticView => statisticView(statistic, key, overrideFor(key), { useDC });
  const hpOverride = overrideFor("hp-max");
  const prompts = collectPrompts(state, mode);
  for (const ability of abilities) {
    if (!ability.choiceKey) continue;
    ability.openPrompts = prompts.filter((p) => p.open && p.choiceKey === ability.choiceKey).map((p) => p.flag);
  }
  const unsupported: UnsupportedRule[] = [];
  const problems: RuleProblem[] = [];
  for (const rule of state.rules) {
    const support = classifyKey(rule.key);
    if (rule.key && (support === "unsupported" || support === "unknown")) unsupported.push({ key: rule.key, support, uuid: rule.item.uuid, name: rule.item.name, index: rule.index, selector: readSelector(rule.source) });
    if (rule.problems.length > 0) problems.push({ key: rule.key, uuid: rule.item.uuid, name: rule.item.name, index: rule.index, messages: [...rule.problems] });
  }
  const fixedCounting = track.fixed.filter((f) => f.countsAgainstSlots).length;
  const free = track.opened.slice(1, level + 1).reduce((sum, n) => sum + n, 0);
  const byLevel: FamiliarSlotsView["byLevel"] = [];
  for (let l = 1; l <= level; l++) byLevel.push({ level: l as LevelNumber, total: track.total[l] ?? 0, opened: track.opened[l] ?? 0 });

  return {
    view: {
      name: settings?.name ?? "Familiar",
      level,
      size: actor.system.traits.size.value,
      traits: [...new Set(actor.system.traits.value)].sort(),
      attribute: { value: attribute, chosen: chosenAttribute, derived: derivedAttribute, modifier: masterAttributeModifier, minimum },
      slots: { total, fixed: fixedCounting, fixedFree: track.fixed.length - fixedCounting, free, filled: abilities.filter((a) => a.choiceKey && a.uuid && !a.extra).length, byLevel },
      hp: { derived: hp.total, override: hpOverride, value: hpOverride?.value ?? hp.total, modifiers: hp.modifiers.map(modifierView), breakdown: breakdownOf(hp.modifiers), overrideKey: "hp-max", recoveryMultiplier: 1, recoveryAddend: 0, negativeHealing: false },
      ac: view(ac, "ac", true),
      saves: Object.fromEntries(SAVES.map((s) => [s, view(saves[s], s)])) as Record<SaveSlug, StatisticView>,
      perception: view(perception, "perception"),
      attack: view(attack, "attack"),
      skills: Object.fromEntries(Object.entries(skills).map(([slug, st]) => [slug, view(st, slug)])),
      speeds: Object.fromEntries(MOVEMENT_TYPES.map((type) => [type, speeds[type] ? speedView(speeds[type]!, `${type}-speed`, overrideFor(`${type}-speed`), actor.system.movement.speeds[type]?.value) : null])) as SpeedViews,
      senses: senseViews(state, actor.system.perception.senses),
      iwr: { immunities: actor.system.attributes.immunities.map((i) => iwrView(state, i)), weaknesses: actor.system.attributes.weaknesses.map((i) => iwrView(state, i)), resistances: actor.system.attributes.resistances.map((i) => iwrView(state, i)) },
      masterDC: { spell: master.actor.system.attributes.spellDC?.value ?? null, class: master.actor.system.attributes.classDC?.value ?? null },
      abilities,
      items: state.items.map((i) => ({ uuid: i.uuid, name: i.name, type: i.type, choiceKey: i.choiceKey ?? null, level: parseChoiceKey(i.choiceKey ?? "")?.level ?? null, rules: i.rules.length, grantedBy: null, openPrompts: prompts.filter((p) => p.open && p.uuid === i.uuid && p.choiceKey === (i.choiceKey ?? null)).map((p) => p.flag), alterations: [...i.applied], inMemory: false, sessionId: null, active: true, overriddenBy: [], badge: null })),
      unsupported,
      problems,
      danglingOverrides: overrides.filter((o) => !used.has(o.statistic)),
      missing,
      rollOptions: state.rollOptions.toJSON(),
    },
    prompts,
    needed: [...needed].sort(),
    track,
  };
}

/** Whether a sheet's Familiar shows: the master has an ability slot or a fixed ability. */
export function hasFamiliar(sheet: Pick<DerivedSheet, "familiar">): boolean {
  return sheet.familiar !== null;
}

